import { EnvironmentInjector, inject, Injectable } from '@angular/core';
import { NavigateToTicketActionDto } from '../model';
import { AgentPanelHostService } from './agent-panel-host.service';

/**
 * Handles the `NavigateToTicket` action: opens the ticket (task) preview in the right sidenav of the
 * current page, or of the tasks dashboard of the target space when the user is elsewhere. Moving the
 * 3D walk to the ticket position (`transformation`) is left to the preview's own "go to position".
 *
 * The preview lives in `feature-task`, which a feature library may not depend on statically; it is
 * loaded lazily like the asset panel.
 */
@Injectable({ providedIn: 'root' })
export class AgentTicketNavigationService {
  private readonly _host = inject(AgentPanelHostService);
  // eslint-disable-next-line @nx/enforce-module-boundaries
  private _featureTaskChunk?: Promise<typeof import('feature-task')>;
  private _panelInjector?: EnvironmentInjector;

  async navigate(action: NavigateToTicketActionDto): Promise<void> {
    const portal = await this._host.portalFor(action.apartmentId, ['/space', action.apartmentId, 'dashboard', 'tasks']);
    if (!portal) throw new Error('[ai-agent] no panel host for the ticket');

    // eslint-disable-next-line @nx/enforce-module-boundaries
    const { TaskPanelService, TaskAccessService } = await (this._featureTaskChunk ??= import('feature-task'));
    // The services are scoped to a page component elsewhere; one instance per opened panel keeps that lifetime.
    this._panelInjector?.destroy();
    this._panelInjector = this._host.createPanelInjector(portal, [TaskPanelService, TaskAccessService]);
    this._panelInjector.get(TaskPanelService).openPreviewById(action.targetId, action.apartmentId, {
      name: action.targetName ?? undefined,
      spaceName: action.apartmentName ?? undefined
    });
  }
}
